import { sleep } from "@captions-web/utils/sleep";
import { z } from "zod";

import { createLocalStorageDef } from "~/lib/local-storage";
import { getConfigStore } from "~/stores/config";

import { getAuthStore } from "../auth/auth-store";

import {
  GetCheckoutInfoResponseType,
  PlanFrequencyType,
  stripeKeyModeEnumSchema,
  StripeKeyModeType,
  SubscribablePlanType,
} from "./network/schema";

export function setStripeKeyMode(mode: StripeKeyModeType) {
  stripeKeyModeStorage.set({ mode });
  window.location.reload();
}

export function getStripeKeyMode() {
  const data = stripeKeyModeStorage.get();

  if (!data) {
    stripeKeyModeStorage.set({ mode: "live" });
    return "live";
  }

  return data.mode;
}

export function getStripeKey() {
  const keyMode = getStripeKeyMode();

  if (keyMode === "live") {
    return getConfigStore().env.STRIPE_PUBLISHABLE_LIVE_KEY;
  }

  return getConfigStore().env.STRIPE_PUBLISHABLE_TEST_KEY;
}

export function clearStripeKeyMode() {
  stripeKeyModeStorage.remove();
}

const stripeKeyModeStorage = createLocalStorageDef(
  "studio_checkout_stripe_key_mode",
  z.object({
    mode: stripeKeyModeEnumSchema,
  })
);

export function getProductAndPriceInfo(
  checkoutInfo: GetCheckoutInfoResponseType,
  plan: SubscribablePlanType,
  frequency: PlanFrequencyType
) {
  const planInfo = checkoutInfo.plans.find((p) => p.name === plan);
  const priceInfo = planInfo?.prices.find((p) => p.frequency === frequency);
  return { planInfo, priceInfo };
}

export async function pollForSubscriptionActivation(targetPlanName: SubscribablePlanType) {
  // we will poll for 5 minutes
  const MAX_ATTEMPTS = 300;

  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    try {
      const response = await getAuthStore().refreshProfile();
      const subscriptionDetails = response?.currentOrg.subscription;

      if (subscriptionDetails?.plan === targetPlanName) {
        return true;
      }
    } catch (error) {
      console.error("Error polling for subscription activation", error);
    }

    await sleep(1000);
  }

  return false;
}

export function getBusinessPlanPriceInfo(checkoutInfo?: GetCheckoutInfoResponseType) {
  const businessPlan = checkoutInfo?.plans.find((plan) => plan.name === "business");

  if (!businessPlan) {
    return null;
  }

  const price = businessPlan.prices.find((price) => price.frequency === "monthly");

  if (!price) {
    return null;
  }

  const numberFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });

  if (price.discountedIntroTerm) {
    return {
      type: "discounted",
      displayedPrice: numberFormatter.format(price.discountedIntroTerm.priceCents / 100),
      duration: price.discountedIntroTerm.duration,
      originalPrice: numberFormatter.format(price.priceCents / 100),
      priceCents: price.discountedIntroTerm.priceCents,
      includedCredits: businessPlan.includedCreditsAmount,
      includedSeats: businessPlan.includedSeatsAmount,
    } as const;
  }

  return {
    type: "regular",
    displayedPrice: numberFormatter.format(price.priceCents / 100),
    priceCents: price.priceCents,
    includedCredits: businessPlan.includedCreditsAmount,
    includedSeats: businessPlan.includedSeatsAmount,
  } as const;
}

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const quantityFormatter = new Intl.NumberFormat();

export function formatCreditsCTA(quantity: number, amountCents: number) {
  return `Buy ${quantityFormatter.format(quantity)} credits • ${currencyFormatter.format(
    amountCents / 100
  )}`;
}

export class CheckoutFormError extends Error {}

/**
 * Extracts incomplete and invalid steps from the form error.
 * @remarks Stripe **ONLY** returns the first field validation error that occurred, so the return
 * value will only contain one step in one of `incomplete_steps` or `invalid_steps` attributes.
 * @param error - The form error.
 * @returns The incomplete and invalid steps.
 */
export function getFormErrorTrackingParameters(error: CheckoutFormError) {
  const incomplete_steps: string[] = [];
  const invalid_steps: string[] = [];
  try {
    const cause = error.cause && typeof error.cause === "object" ? error.cause : {};
    const type = "type" in cause ? cause.type : "";
    const code = "code" in cause ? cause.code : "unknown";
    if (type === "validation_error" && typeof code === "string") {
      const matches = code.match(/(incomplete|invalid)_(\w+)/);
      if (matches) {
        const [, error_type, step] = matches;
        let stepName = step;
        switch (step) {
          case "number":
            stepName = "Card number";
            break;
          case "expiry":
            stepName = "Expiration date";
            break;
          case "cvc":
            stepName = "Security code";
            break;
          case "zip":
            stepName = "ZIP code";
            break;
        }
        if (error_type === "incomplete") {
          incomplete_steps.push(stepName);
        } else if (error_type === "invalid") {
          invalid_steps.push(stepName);
        }
      } else {
        incomplete_steps.push(code);
      }
    }
  } catch (error) {
    // ignore
  }

  return {
    incomplete_steps,
    invalid_steps,
    error_message: error.message,
  };
}
